/**
 * BESTIX v5.0 — Article Detail Page
 * Full editorial article view with verdict
 */

import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, Share2, Bookmark, ArrowLeft, ChevronRight } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ArticleCard, ArticleVerdict } from '@/components/magazine';
import { getArticleBySlug, articles } from '@/services/magazineData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <Navigate to="/magazine" replace />;
  }

  // Get related articles (same category or random)
  const relatedArticles = articles
    .filter(a => a.slug !== slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <article>
        {/* Hero */}
        <header className="relative">
          {/* Back Button */}
          <div className="absolute top-6 left-6 z-20">
            <Link
              to="/magazine"
              className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-[var(--color-navy)] text-sm font-medium hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Magazine
            </Link>
          </div>

          {/* Hero Image */}
          <div className="relative h-[50vh] md:h-[60vh]">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 bg-[var(--color-gold)] text-[var(--color-navy)] rounded text-xs font-bold uppercase tracking-wide mb-4">
                {article.category}
              </span>
              <h1 className="font-playfair text-3xl md:text-5xl lg:text-6xl text-white !text-white mb-4">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="font-playfair italic text-xl md:text-2xl text-[var(--color-gold)] mb-6">
                  {article.subtitle}
                </p>
              )}
              
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <span>{article.author}</span>
                <span>•</span>
                <span>{article.publishedAt}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {article.readTime} min read
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 py-12">
          {/* Share Bar */}
          <div className="flex items-center justify-between mb-8 pb-8 border-b">
            <div className="flex items-center gap-3">
              <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                <Bookmark className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Article Body */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-playfair prose-headings:text-[var(--color-navy)]
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-[var(--color-gold)] prose-a:no-underline hover:prose-a:underline
              prose-blockquote:border-l-[var(--color-gold)] prose-blockquote:border-l-4 prose-blockquote:bg-[var(--color-gold)]/5 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:text-[var(--color-navy)]
              prose-strong:text-[var(--color-navy)]
              prose-li:text-gray-700
              prose-ul:list-disc prose-ol:list-decimal
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Verdict Box */}
          {article.verdict && (
            <ArticleVerdict {...article.verdict} className="mt-12" />
          )}

          {/* Author Bio */}
          <div className="mt-12 p-6 bg-gray-50 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-[var(--color-navy)] flex items-center justify-center text-white font-bold text-xl">
                B
              </div>
              <div>
                <p className="font-medium text-[var(--color-navy)]">{article.author}</p>
                <p className="text-sm text-gray-500 mt-1">
                  BESTIX Editorial crafts guides for people who'd rather be out there than reading about it—but want to make sure the night's worth it first.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-gray-50 py-16 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-playfair italic text-2xl text-[var(--color-navy)]">Keep Reading</h2>
                <Link
                  to="/magazine"
                  className="flex items-center gap-1 text-[var(--color-gold)] hover:opacity-80 font-medium"
                >
                  All Articles
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.slug} {...related} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[var(--color-navy)] py-16 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="font-playfair italic text-2xl text-white !text-white mb-4">
              Ready to Go?
            </h2>
            <p className="text-white/70 mb-8">
              Put this knowledge to use. Find your next unforgettable night.
            </p>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-gold)] text-[var(--color-navy)] rounded-full font-medium hover:bg-[var(--color-gold-light)] transition-colors"
            >
              Browse Events
            </Link>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
